import React from "react";
import { Navigate } from "react-router";
import { usePermissionContext } from "@/context/PermissionContext";
import { RoleLevel } from "@/types/permissions";
import { PermissionDenied } from "./PermissionComponents";

/**
 * Route guard component that protects routes based on permissions
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  resource?: string;
  action?: string;
  page?: string;
  minimumRole?: RoleLevel;
  fallbackPath?: string;
  showDeniedPage?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  resource,
  action,
  page,
  minimumRole,
  fallbackPath = "/",
  showDeniedPage = true,
}) => {
  const { hasPermission, hasPageAccess, getRoleLevel } = usePermissionContext();

  // Check specific resource/action permission
  if (resource && action) {
    if (!hasPermission(resource, action)) {
      return showDeniedPage ? (
        <PermissionDenied resource={resource} action={action} />
      ) : (
        <Navigate to={fallbackPath} replace />
      );
    }
  }

  // Check page access
  if (page) {
    if (!hasPageAccess(page)) {
      return showDeniedPage ? (
        <PermissionDenied page={page} />
      ) : (
        <Navigate to={fallbackPath} replace />
      );
    }
  }

  // Check minimum role level
  if (minimumRole !== undefined) {
    if (getRoleLevel() < minimumRole) {
      const getRoleName = (level: RoleLevel) => {
        switch (level) {
          case 1:
            return "External";
          case 2:
            return "Contributor";
          case 3:
            return "Project Manager";
          case 4:
            return "Admin";
          default:
            return "Unknown";
        }
      };

      return showDeniedPage ? (
        <PermissionDenied minimumRole={getRoleName(minimumRole)} />
      ) : (
        <Navigate to={fallbackPath} replace />
      );
    }
  }

  return <>{children}</>;
};

/**
 * HOC for protecting components with permissions
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  permissionCheck: {
    resource?: string;
    action?: string;
    page?: string;
    minimumRole?: RoleLevel;
  },
) {
  return function PermissionProtectedComponent(props: P) {
    return (
      <ProtectedRoute {...permissionCheck}>
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };
}
